import { useCallback, useEffect, useMemo, useState } from "react";
import DomToImage from "dom-to-image";
import {
  Avatar,
  Button,
  Checkbox,
  Divider,
  Group,
  Loader,
  LoadingOverlay,
  Modal,
  NumberFormatter,
  Stack,
  Text,
} from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { pdf } from "@react-pdf/renderer";
import ReportDocument from "./ReportingDocument";
import ReportingSummary from "../summary/ReportingSummary";
import LinkedinSummaryTop from "../reportingLinkedin/summary/LinkedinSummaryTop";
import LinkedinSummary from "../reportingLinkedin/summary/LinkedinSummary";
import EmailSummaryMulti from "../reportingEmail/summary/EmailSummaryMulti";
import EmailSummary from "../reportingEmail/summary/EmailSummary";
import { useReportingStore } from "../../../../stores/ReportingStore";
import EmailPerf from "../reportingEmail/perf/EmailPerf";
import ActivitySummaryMulti from "../activities/summary/ActivitySummaryMulti";
import ActivitySummary from "../activities/summary/ActivitySummary";
import ReportingSendersAllExport from "../senders/ReportingSendersAllExport";
import { useLanguage } from "../../../../stores/LanguageStore";
import LeadsSummaryMulti from "../leads/summary/LeadsSummaryMulti";
import LeadsSummary from "../leads/summary/LeadsSummary";
import { downloadBlob } from "../../../../functions/utilFunctions";
import { useProfileStore } from "../../../../stores/UserStore";

function splitArrayIntoChunks(array, chunkSize) {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

const ReportingExporter = () => {
  const lang = useLanguage((s) => s.language);
  const profile = useProfileStore((s) => s.profile);
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);

  const actualStatsDate = useReportingStore((s) => s.actualStatsDate);
  const programmations = useReportingStore((s) => s.programmations);
  const globalStats = useReportingStore((s) => s.globalStats);

  const loadingStats = useReportingStore((s) => s.loading);
  const loadingDate = useReportingStore((s) => s.loadingDate);
  const loadingProgs = useReportingStore((s) => s.loadingProgStats);

  const statistics = useMemo(() => {
    const totals = actualStatsDate.reduce((acc, curr) => {
      Object.keys(curr).forEach((key) => {
        if (key !== "date") {
          acc[key] = (acc[key] || 0) + curr[key];
        }
      });
      return acc;
    }, {});
    return totals;
  }, [actualStatsDate]);

  const handleOpen = () => {
    setOpened(true);
    setLoading(false);
  };

  const handleClose = () => {
    setOpened(false);
    setLoading(false);
  };

  const delivrabilityChunks = useMemo(
    () =>
      splitArrayIntoChunks(globalStats?.integrations_deliverability || [], 26),
    [globalStats.integrations_deliverability]
  );

  const toDownload = useMemo(() => {
    let result = [
      { value: "profile", label: lang.profile.main_title },
      { value: "general-summary", label: lang.reports.campaign_summary },
      { value: "email-activity", label: lang.reports.activity_email },
      { value: "linkedin-activity", label: lang.reports.activity_linkedin },
      { value: "leads-classification", label: lang.reports.leads_summary },
    ];

    delivrabilityChunks.forEach((chunk, index) => {
      result.push({
        value: "delivrability-" + index,
        label:
          lang.reports.delivrability +
          " " +
          lang.reports.page +
          " " +
          (index + 1),
      });
    });

    return result;
  }, [delivrabilityChunks, lang]);

  const [downloadList, setDownloadList] = useState(
    toDownload.map((x) => x.value)
  );

  useEffect(() => {
    setDownloadList(toDownload.map((x) => x.value));
  }, [toDownload]);

  const handleExport = useCallback(async () => {
    setLoading(true);
    let blobs = [];
    let requests = [];

    downloadList
      .filter((x) => x !== "profile")
      .forEach((item) => {
        requests.push(
          new Promise((resolve, reject) => {
            DomToImage.toBlob(document.getElementById(item)).then((blob) => {
              blobs.push(blob);

              resolve(blob);
            });
          })
        );
      });

    Promise.allSettled(requests).then((res) => {
      let newDocumentBlobs = res.map((x) => x.value);
      const Doc = <ReportDocument imageBlobs={newDocumentBlobs} />;

      pdf(Doc)
        .toBlob()
        .then((b) => {
          downloadBlob(b, "Reporting - export");
        })
        .finally(() => {
          setLoading(false);
        });
    });
  }, [downloadList]);

  return (
    <>
      <Button
        leftSection={<IconDownload size={20} />}
        size="md"
        ml={32}
        onClick={handleOpen}
        loading={loading}
        disabled={loadingStats || loadingDate || loadingProgs}
      >
        {lang.reports.export_button_label}
      </Button>

      <Modal
        title={lang.reports.export_title}
        opened={opened}
        onClose={handleClose}
        size={"100%"}
      >
        <LoadingOverlay
          visible={loading}
          pos={"sticky"}
          top={0}
          w={"100%"}
          h={"85vh"}
          overlayProps={{ blur: 2 }}
          loaderProps={{
            children: (
              <Stack align="center">
                <Loader />

                <Text size="xl">{lang.reports.generating}</Text>
              </Stack>
            ),
          }}
        />
        <Stack
          gap={"xl"}
          style={{
            background: "var(--mantine-color-primary-2)",
            borderRadius: 8,
          }}
          pos={"relative"}
        >
          <Stack id="general-summary" p={"xl"} gap={"xl"} pos={"relative"}>
            {downloadList.includes("profile") && (
              <Group
                gap={4}
                p={12}
                wrap="nowrap"
                id="profile"
                pos={"absolute"}
                style={{ zIndex: 300 }}
                top={8}
                right={16}
              >
                <Avatar src={profile?.avatar} size={52} />
                <Stack ml={"sm"} ta={"start"} gap={0} mr={8} c={"black"}>
                  <Text fw={600} truncate="end" size="lg">
                    {profile?.first_name} {profile?.last_name}
                  </Text>
                  <Text opacity={0.5}>#{profile?.id}</Text>
                </Stack>
              </Group>
            )}

            <ReportingSummary />

            <Text fw={600} size="xl" ta={"center"} mt={"xl"}>
              {lang.reports.global_activity}
            </Text>
            <Text size="lg" ta={"center"} mt={-24}>
              <NumberFormatter
                value={statistics.email_sent}
                style={{ fontWeight: 700 }}
              />{" "}
              {lang.reports.email_sent}{" "}
              <NumberFormatter
                value={statistics.linkedin_sent}
                style={{ fontWeight: 700 }}
              />{" "}
              {lang.reports.linkedin_actions}
            </Text>

            {programmations.length > 1 ? (
              <ActivitySummaryMulti />
            ) : (
              <ActivitySummary />
            )}
          </Stack>

          <Divider />

          <Stack id="email-activity" p={"xl"} gap={"xl"}>
            <Text fw={600} size="xl" ta={"center"}>
              {lang.reports.emails}
            </Text>
            <Text size="lg" ta={"center"} mt={-24}>
              <NumberFormatter
                value={statistics.email_sent}
                style={{ fontWeight: 700 }}
              />{" "}
              {lang.reports.email_sent}
            </Text>

            {programmations.length > 1 ? (
              <EmailSummaryMulti />
            ) : (
              <EmailSummary />
            )}

            <EmailPerf />
          </Stack>

          <Divider />

          <Stack id="linkedin-activity" p={"xl"} gap={"xl"}>
            <Text fw={600} size="xl" ta={"center"}>
              LinkedIn
            </Text>

            <LinkedinSummaryTop isExport />

            <LinkedinSummary
              type={{
                field: "linkedin_message_sent",
                label: lang.reports.message_sent,
                type: "messages",
              }}
            />
            <LinkedinSummary
              type={{
                field: "linkedin_invitation_sent",
                label: lang.reports.invitation_sent,
                type: "invitations",
              }}
            />
            <LinkedinSummary
              type={{
                field: "linkedin_visit_profile",
                label: lang.reports.visited_profiles,
                type: "visits",
              }}
            />
          </Stack>

          <Divider />

          <Stack id="leads-classification" p={"xl"} gap={"xl"}>
            <Text fw={600} size="xl" ta={"center"}>
              {lang.reports.leads_summary}
            </Text>
            {programmations.length > 1 ? (
              <LeadsSummaryMulti />
            ) : (
              <LeadsSummary />
            )}
          </Stack>

          <Divider />

          {splitArrayIntoChunks(
            globalStats?.integrations_deliverability || [],
            26
          ).map((rows, i) => (
            <Stack key={i} id={"delivrability-" + i} p={"xl"} gap={"xl"}>
              <Text fw={600} size="xl" ta={"center"}>
                {lang.reports.delivrability} ( {lang.reports.page} {i + 1} )
              </Text>
              <ReportingSendersAllExport id={"table-" + i} rows={rows} />
            </Stack>
          ))}
        </Stack>

        <Group
          justify="space-between"
          gap={"xl"}
          py={16}
          pos={"sticky"}
          bottom={-1}
          bg={"#fff"}
          style={{ zIndex: 110 }}
          wrap="nowrap"
        >
          <Group
            bg={"#fff"}
            style={{ borderRadius: 8, zIndex: 10 }}
            wrap="wrap"
          >
            {toDownload.map((item) => (
              <Checkbox
                key={item.value}
                label={item.label}
                checked={downloadList.includes(item.value)}
                size="md"
                disabled={loading}
                onChange={(e) => {
                  setDownloadList(
                    e.target.checked
                      ? [...downloadList, item.value].sort(
                          (a, b) =>
                            toDownload.findIndex((x) => x.value === a) -
                            toDownload.findIndex((x) => x.value === b)
                        )
                      : downloadList.filter((x) => x !== item.value)
                  );
                }}
              />
            ))}
          </Group>
          <Button
            leftSection={<IconDownload size={20} />}
            size="lg"
            onClick={handleExport}
          >
            {loading ? lang.reports.please_wait : lang.reports.download_label}
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default ReportingExporter;
